import {
  GiveawayOperatorStatus,
  GiveawayStatus,
  RaffleV2Product,
} from 'jigsaw';
import { FC } from 'react';
import { GiveawayTimeRemainingView } from '../giveaway/GiveawayTimeRemaining';
import { getEmojiFlag, TCountryCode } from 'countries-list';
import { Progress, Text, theme } from 'ui_2';
import { BiCopy } from 'react-icons/bi';
import toast from 'react-hot-toast';

// --- GiveawayCard Component ---
export type GiveawayCardProps = {
  giveaway: RaffleV2Product &
    GiveawayStatus & {
      uniqueEntries: number;
      totalEntries: number;
    };
  errorMsg?: string;
  isGiveawayClosed: boolean;
  isGiveawayOver: boolean;
  onShowGraph: () => void;
};

export const GiveawayCard: FC<GiveawayCardProps> = ({
  giveaway,
  errorMsg,
  isGiveawayClosed,
  isGiveawayOver,
  onShowGraph,
}) => {
  const smallImageURL =
    giveaway.productSku?.service.smallLogoImageURL ??
    giveaway.productSku?.smallImageURL;

  const handleCopyId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(
        giveaway.giveawayId ?? giveaway.programId
      );
      toast.success(
        `Copied ${giveaway.giveawayId ? 'giveawayId' : 'programId'}`
      );
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div
      className='relative flex w-full cursor-pointer flex-col rounded-xl border border-gray-100 bg-white p-4 shadow-md transition-shadow hover:shadow-lg'
      onClick={onShowGraph}
    >
      {/* Time Remaining Badge */}
      <div className='absolute right-4 top-4 z-10'>
        <GiveawayTimeRemainingView
          cutoff={giveaway.cutoff}
          winnerCount={giveaway.winnerCount}
          isGiveawayClosed={isGiveawayClosed}
          isGiveawayOver={isGiveawayOver}
        />
      </div>

      {/* Header with Image and Title */}
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-start'>
        {smallImageURL && (
          <img
            src={smallImageURL}
            className='h-14 w-14 self-center rounded-lg border border-gray-200 object-cover sm:self-start'
            alt='Giveaway'
          />
        )}
        <div className='flex flex-1 flex-col gap-1 pr-16 text-center sm:text-left'>
          <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
            <Text font='bold-md'>
              {giveaway.title}{' '}
              {giveaway.productSku?.countries?.[0] &&
                getEmojiFlag(giveaway.productSku.countries[0] as TCountryCode)}
            </Text>
            <button
              onClick={handleCopyId}
              className='self-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 sm:self-start'
            >
              <BiCopy />
            </button>
          </div>
          {giveaway.productSku?.title && (
            <Text font='medium-sm' color='gray-500'>
              {giveaway.productSku.title} - {giveaway.winnerCount}{' '}
              {giveaway.winnerCount > 1 ? 'winners' : 'winner'}
            </Text>
          )}
        </div>
      </div>

      {/* Date Range Section */}
      <div className='mb-4 rounded-md bg-blue-50 p-3'>
        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4'>
          {giveaway.start && (
            <div className='flex items-center gap-2'>
              <Text font='medium-xs' color='gray-600'>
                Start:
              </Text>
              <Text font='bold-xs'>
                {new Date(giveaway.start).toLocaleDateString()} at{' '}
                {new Date(giveaway.start).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Text>
            </div>
          )}
          <div className='flex items-center gap-2'>
            <Text font='medium-xs' color='gray-600'>
              {giveaway.start ? 'End:' : 'Cutoff:'}
            </Text>
            <Text font='bold-xs'>
              {new Date(giveaway.cutoff).toLocaleDateString()} at{' '}
              {new Date(giveaway.cutoff).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </div>
        </div>
      </div>

      {/* Status Section */}
      <div className='mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3'>
        {giveaway.openStatus && (
          <div className='rounded-md bg-gray-50 p-2'>
            <Text font='medium-xs' color='gray-600'>
              Open Status
            </Text>
            <Text font='bold-xs'>{giveaway.openStatus}</Text>
          </div>
        )}
        {giveaway.onChainStatus && (
          <div className='rounded-md bg-gray-50 p-2'>
            <Text font='medium-xs' color='gray-600'>
              Onchain Status
            </Text>
            <Text font='bold-xs'>{giveaway.onChainStatus}</Text>
          </div>
        )}
        {giveaway.operatorStatus && (
          <div className='rounded-md bg-gray-50 p-2'>
            <Text font='medium-xs' color='gray-600'>
              Operator Status
            </Text>
            <Text font='bold-xs'>{giveaway.operatorStatus}</Text>
          </div>
        )}
      </div>

      {/* Progress Bars */}
      {giveaway.operatorStatus === GiveawayOperatorStatus.minting && (
        <div className='mb-4 flex flex-col gap-1'>
          <div className='flex justify-between text-xs'>
            <Text font='medium-xs'>
              <b>Mint status:</b> {giveaway.prizesMintedCount} /{' '}
              {giveaway.winnerCount}
            </Text>
            <Text font='medium-xs'>
              {Math.round(
                ((giveaway.prizesMintedCount || 0) / giveaway.winnerCount) * 100
              )}
              %
            </Text>
          </div>
          <Progress
            value={
              ((giveaway.prizesMintedCount || 0) / giveaway.winnerCount) * 100
            }
            color={theme.colors.green[600]}
          />
        </div>
      )}
      {giveaway.operatorStatus === GiveawayOperatorStatus.drawing && (
        <div className='mb-4 flex flex-col gap-1'>
          <div className='flex justify-between text-xs'>
            <Text font='medium-xs'>
              <b>Draw status:</b> {giveaway.winnersDrawnCount} /{' '}
              {giveaway.winnerCount}
            </Text>
            <Text font='medium-xs'>
              {Math.round(
                ((giveaway.winnersDrawnCount || 0) / giveaway.winnerCount) * 100
              )}
              %
            </Text>
          </div>
          <Progress
            value={
              ((giveaway.winnersDrawnCount || 0) / giveaway.winnerCount) * 100
            }
            color={theme.colors.green[600]}
          />
        </div>
      )}
      {giveaway.operatorStatus === GiveawayOperatorStatus.sendingPrizes && (
        <div className='mb-4 flex flex-col gap-1'>
          <div className='flex justify-between text-xs'>
            <Text font='medium-xs'>
              <b>Sent status:</b> {giveaway.prizesSentCount} /{' '}
              {giveaway.winnerCount}
            </Text>
            <Text font='medium-xs'>
              {Math.round(
                ((giveaway.prizesSentCount || 0) / giveaway.winnerCount) * 100
              )}
              %
            </Text>
          </div>
          <Progress
            value={
              ((giveaway.prizesSentCount || 0) / giveaway.winnerCount) * 100
            }
            color={theme.colors.green[600]}
          />
        </div>
      )}
      {errorMsg && (
        <div className='mb-4'>
          <Text font='medium-xs' color='red'>
            Error: {errorMsg}
          </Text>
        </div>
      )}

      {/* Entries Section with Graph Button */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
        <div className='flex flex-col gap-2 sm:flex-row sm:gap-6'>
          <div className='rounded-md bg-gray-50 p-2'>
            <Text font='medium-xs' color='gray-600'>
              Unique entries
            </Text>
            <Text font='bold-sm'>
              {giveaway.uniqueEntries.toLocaleString()}
            </Text>
          </div>
          <div className='rounded-md bg-gray-50 p-2'>
            <Text font='medium-xs' color='gray-600'>
              Total entries
            </Text>
            <Text font='bold-sm'>{giveaway.totalEntries.toLocaleString()}</Text>
          </div>
        </div>

        {/* Graph Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShowGraph();
          }}
          className='rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 transition-colors hover:bg-green-200'
        >
          📈 View Graph
        </button>
      </div>
    </div>
  );
};
